import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { beachActions, beachSelectors } from "../../redux";

interface useSeatProps {
  selectedSeat?: {
    progressiveNumber: number;
    row: number;
  };
  beachId: string;
  maxPeople: number;
}

const useSeat = ({ selectedSeat, beachId, maxPeople }: useSeatProps) => {
  const getSeatIdByMatrix = useSelector(beachSelectors.getSeatIdByMatrix);
  const dispatch = useDispatch();

  const seat = useMemo(() => {
    return (
      selectedSeat &&
      getSeatIdByMatrix(selectedSeat.progressiveNumber, selectedSeat.row)
    );
  }, [getSeatIdByMatrix, selectedSeat]);

  const [
    selectedSeatMaxReservations,
    setSelectedSeatMaxReservations,
  ] = useState(maxPeople);

  useEffect(() => {
    if (seat?.maxPeople) {
      setSelectedSeatMaxReservations(seat.maxPeople);
    } else {
      setSelectedSeatMaxReservations(maxPeople);
    }
  }, [maxPeople, seat, selectedSeat]);

  const handleDeleteSeat = useCallback(() => {
    if (seat) {
      dispatch(beachActions.deleteSeat({ seatId: seat.id, beachId: beachId }));
    }
  }, [beachId, dispatch, seat]);

  const handleSeat = useCallback(
    (max: number, seatId?: string) => {
      if (!seatId) {
        dispatch(
          beachActions.createSeat({
            beachId,
            seat: {
              progressiveNumber: selectedSeat?.progressiveNumber,
              row: selectedSeat?.row,
              maxPeople: max,
            },
          })
        );
      } else {
        dispatch(
          beachActions.updateSeat({
            beachId,
            seat: {
              id: seatId,
              progressiveNumber: selectedSeat?.progressiveNumber,
              row: selectedSeat?.row,
              maxPeople: max,
            },
          })
        );
      }
    },
    [beachId, dispatch, selectedSeat]
  );

  const handleNumberOfSeats = useCallback(
    (n: number) => () => {
      let current = n + selectedSeatMaxReservations;

      if (current >= 1 && current <= maxPeople) {
        setSelectedSeatMaxReservations(current);
        if (seat?.id) {
          handleSeat(current, seat.id);
        }
      }
    },
    [handleSeat, maxPeople, seat, selectedSeatMaxReservations]
  );
  return {
    handleSeat,
    handleNumberOfSeats,
    handleDeleteSeat,
    selectedSeatMaxReservations,
    setSelectedSeatMaxReservations,
    seat,
  };
};

export default useSeat;
